import { Resource } from 'entities/hallmap/model/types';
import { Person } from 'entities/person';
import { CustomError } from 'shared/api';

export type Status =
	| 'new'
	| 'appointed'
	| 'visit_started'
	| 'visit_ended'
	| 'visit_missed'
	| 'waiting_list'
	| 'cancelled'
	| 'in_progress'
	| 'visit_missed';

export enum State {
	WAITING = 'waiting',
	DELAY = 'delay',
	ENDING = 'ending',
	EXCEEDING = 'exceeding',
	BUSY = 'busy',
}

type SortBy = 'dt_start';

export interface OrdersByDateTimeParams {
	dt_start?: string;
	status?: Status;
}

export interface Filter {
	appointment__resources?: string[];
	participants_count__gte?: string;
	participants_count__lte?: string;
	tags?: string[];
	status?: Status[];
	places?: string[];
	dt_start__gte?: string;
	dt_end__lte?: string;
	page?: number;
	is_confirmed?: boolean[] | boolean;
	ordering?: SortBy;
	search?: string;
  is_guest_appointment?: boolean;
  idempotencyKey?: string;
	[key: string]: any;
}

export interface OrdersByStatusParams {
	status: Status;
}

export interface OrdersResponce {
	results: Order[];
	stats: {
		appointments: number | null;
		participants: number | null;
	}
}

export interface ExcelResponce {
  "id": number,
  "status": "PENDING" | 'COLLECTED',
  "uid"?: string,
  "model"?: string,
  "filters"?: string,
  "file"?: string,
  "dt_created"?: string,
  "dt_collected"?: string,
  "user"?: number
}

export interface PostOrderResponce {
	order: Order[];
	status: number;
	errors: string;
}

export interface OrderGroup {
	date: string;
	orders: {
		time: string;
		ordersList: Order[];
	}[];
}

export interface OrderGroupByDay {
	date: string;
	orders: Order[];
}

export interface OrdersState {
	orders: Order[];
	waitingOrders: Order[];
	ordersGroups: OrderGroup[];
	ordersByTable: Order[];
	orderDetail: Order | null;
	orderInfoDetail: Order | null;
	orderDetailPerson: Person | null;
	orderInfoDetailPerson: Person | null;
	ordersDelayed: Order[];
	ordersByVisit: Order[];
	ordersByPerson: Order[];
	ordersByDate: OrderGroup[];
	orderBookingData: OrderBookingData;
	orderBookingDataInitial: OrderBookingData | null | undefined;
	bookingThreadMod?: boolean;
	bookingApplicationMod?: boolean;
	bookingIsNew?: boolean;
	bookingIsEdit?: boolean;
	selectedOrderId: number;
	selectedOrderInfoId?: number;
	loadedTable: string | null;
	loading: boolean;
	loadingDetail: boolean;
	loadingOrderGroups: boolean;
	loadingWaitingOrders: boolean;
	loadingOrdersByPerson: boolean;
	loadingError: string;
	loadingNext: boolean;
	ordersDelayedNext: Order[];
	ordersWaitingNext: OrderGroup[];
	ordersSearchNext: string;
	errorOrdersByPerson: CustomError | null;
	error: CustomError | null;
	reset: boolean;
	stats: {
		appointments: number | null | string;
		participants: number | null | string;
	},
  paymentProviders: PaymentProvider[];
  activeProvider: PaymentProvider | null;
  allowDepositeActions: boolean;
  toBookingButtonDisabled: boolean;
}

export interface OrderBookingData {
	id?: number;
	notes: string;
	prepayment_amount: string;
	person: OrderBookingPerson;
	tags: string[];
	dt_start: string;
	duration: number;
	participants_count: number;
	resources: number[];
	bypass: boolean;
	idempotencyKey?: string;
	auto_start_visit?: boolean;
	hold_resources: boolean;
  deposit?: Deposit | null;
  is_confirmed?: boolean | null;
}
export interface OrderBookingDataPatch {
  id?: number;
  body: {
    notes: string;
    prepayment_amount: string;
    person: OrderBookingPerson;
    tags: string[];
  };
}

export type TFieldsForUpdateOrder = Partial<
	Omit<OrderBookingData, 'dt_start' | 'id'>
> & { dt_start: string };

export interface OrderBookingPerson
	extends Omit<Person, 'id' | 'email' | 'uid' | 'dt_created'> {
	id?: number;
}

export type Appointment = {
	id: number;
	resources: Resource[];
	uid: string;
	dt_created: string;
	dt_start: string;
	dt_end: string;
	duration: number;
	participants_count: number;
	dt_visit_start: string;
	dt_visit_end: string;
	hold_resources: boolean;
	bypass: boolean;
};

export type Order = {
	id: number;
	conflicts: Resource[] | null;
	appointment: Appointment;
	person: Person;
	uid: string;
	dt_created: string;
	dt_start: string;
	dt_end: string;
	duration: number;
	participants_count: number;
	prepayment_amount: number | null;
	prepayment_method: string;
	has_preorder: boolean;
	notes: string;
	status: Status;
	state: State;
	tags: string[];
	[key: string]: any;
  deposit?: Deposit | null;
};

export type CalculateDepositeData = {
  dt_start: string;
	duration: number;
	participants_count: number;
};

export type CalculateDepositeResponce = {
  result: boolean;
  rule_id?: number;
  amount?: number | string;
  payment_due_date?: string;
  description_for_staff?: string;
  description_for_public?: string;
};

export type PaymentProvider =  {
  id: number,
  backend: {
    uid: string,
    verbose_name: string,
    icon: string,
    is_managed: boolean
  }
}

export type Deposit = {
  provider_id?: number,
  amount?: number | string,
  due_date?: string,
  id?: number,
  user?: {
    id?: number,
    username?: string,
    name?: string
  },
  provider?: PaymentProvider,
  uid?: string,
  dt_created?: string,
  comment?: string | null,
  dt_paid?: boolean | null,
  payment_uid?: string | null,
  payment_details?: string | null,
  payment_url?: string | null,
  status?: string
}
